// src/components/common/PlotContainer.jsx
import { result } from "lodash";
import React, { useState, useEffect, forwardRef } from "react";
import { Card, CardBody, CardHeader, Spinner, Alert, Button } from "reactstrap";
import DataTable from "react-data-table-component";
import DynamicIframe from "./DynamicIframe";


// Use forwardRef so we can target the iframe reference on the original page
const PlotContainer = forwardRef(function PlotContainer(
   {
     plotData,
     loading,
     error,
     selectedFilesCount = 0,
     title = "Plot Results",
     onRetry,
     showFileCount = true,
     isTable = false,
   },
   ref
 ) {
  const [plotUrl, setPlotUrl] = useState(null);
  const [copied, setCopied] = useState(false);
  const [filterText, setFilterText] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const stripHtml = (html) => {
    if (!html) return "";
    const div = document.createElement("div");
    div.innerHTML = html;
    return (div.textContent || div.innerText || "").trim();
  };

  // Given a single value, return null if value is string
  const testNumericVal = (v) => {
    if (v == null) return null;
    // convert value to string, remove all commas & whitespaces
    const s = String(v).replace(/,/g, "").trim();
    if (s === "") return null;
    // convert s to number
    const n = Number(s);
    return Number.isFinite(n) ? n : null;
  };

  // Given values from a column, return true if the majority of values are numeric
  const testNumericCol = (rows, selector, sampleSize = 30) => {
    let seen = 0;
    let numeric = 0;

    for (let i = 0; i < rows.length && seen < sampleSize; i++) {
      const val = selector(rows[i]);
      const n = testNumericVal(val);
      if (val == null || String(val).trim() === "") continue;
      seen++;
      if (n !== null) numeric++;
    }

    // if most samples are numeric, treat as numeric column
    return seen > 0 && numeric / seen > 0.8;
  };

  // Used by data table, allows for numeric columns like counts to be sorted numerically
  // when clicking on headers, rather than alphabetically
  const customColumnSort = (rows, selector, direction) => {
    const dir = direction === "desc" ? -1 : 1;
    const numericCol = testNumericCol(rows, selector);
    const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: "base" });

    return [...rows].sort((a, b) => {
      const av = selector(a);
      const bv = selector(b);

      // Empty cells at bottom
      const aBlank = av == null || String(av).trim() === "";
      const bBlank = bv == null || String(bv).trim() === "";
      if (aBlank && bBlank) return 0;
      if (aBlank) return 1;
      if (bBlank) return -1;

      if (numericCol) {
        const an = testNumericVal(av);
        const bn = testNumericVal(bv);

        // If one is numeric and other is not, put the non-numric last
        if (an === null && bn === null) return 0;
        if (an === null) return 1;
        if (bn === null) return -1;

        return (an - bn) * dir;
      }

      return collator.compare(String(av), String(bv)) * dir;
    });
  };


  const cellToSortableText = (cell) => {
    if (!cell) return "";
    if (cell.type === "link") return (cell.text || "").trim();
    if (cell.type === "html") return stripHtml(cell.content);
    return (cell.text || "").trim();
  };

  const renderCell = (cell) => {
    if (!cell) return null;

    if (cell.type === "link") {
      return (
        <a
          href={cell.href}
          target={cell.target}
          rel="noopener noreferrer"
          style={{
            color: "#2F9E37",
            textDecoration: "underline",
            fontSize: "11px",
            fontWeight: "500",
          }}
        >
          {cell.text}
        </a>
      );
    }

    if (cell.type === "html") {
      return <span dangerouslySetInnerHTML={{ __html: cell.content }} />;
    }

    return (
      <span
        style={{
          fontFamily: cell.text && cell.text.match(/^[ATCGU]+$/) ? "monospace" : "inherit",
        }}
      >
        {cell.text}
      </span>
    );
  };

  const dataTableStyles = {
    headRow: {
      style: {
        backgroundColor: "#2F9E37",
        borderTopLeftRadius: "8px",
        borderTopRightRadius: "8px",
      },
    },
    headCells: {
      style: {
        color: "white",
        fontSize: "12px",
        fontWeight: 600,
      },
    },
    cells: {
      style: {
        fontSize: "11px",
        paddingTop: "10px",
        paddingBottom: "10px",
      },
    },
  };


  const handleCopyShortCode = async (short_code) => {
    var short_url = process.env.REACT_APP_API_URL + "/short/" + short_code
    try {

      await navigator.clipboard.writeText(short_url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      // Fallback if clipboard API is unavailable
      const ta = document.createElement("textarea");
      ta.value = short_url;
      ta.style.position = "fixed";
      ta.style.left = "-9999px";
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);

      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    }
  };

  useEffect(() => {
    if (plotData && !isTable) {
      let content = plotData;
      let contentType = "text/html";

      if (typeof plotData === "object") {
        content = `
          <html>
            <head>
              <style>
                body { 
                  margin: 0; 
                  padding: 0; 
                  font-family: system-ui, -apple-system, sans-serif;
                }
                
                ::-webkit-scrollbar {
                  width: 8px;
                  height: 8px;
                }
                ::-webkit-scrollbar-track {
                  background: #f1f1f1;
                  border-radius: 4px;
                }
                ::-webkit-scrollbar-thumb {
                  background: #2f9e37;
                  border-radius: 4px;
                  transition: background-color 0.2s ease;
                }
                ::-webkit-scrollbar-thumb:hover {
                  background: #238b2f;
                }
                ::-webkit-scrollbar-corner {
                  background: #f1f1f1;
                }
                
                * {
                  scrollbar-width: thin;
                  scrollbar-color: #2f9e37 #f1f1f1;
                }
              </style>
            </head>
            <body>
              <pre style="padding: 20px; background: #f8f9fa; border-radius: 6px; overflow: auto; height: 100vh; box-sizing: border-box;">
                ${JSON.stringify(plotData, null, 2)}
              </pre>
            </body>
          </html>
        `;
      } else {
        if (content.includes("<html>") || content.includes("<HTML>")) {
          const styleTag = `
            <style>
              ::-webkit-scrollbar {
                width: 8px;
                height: 8px;
              }
              ::-webkit-scrollbar-track {
                background: #f1f1f1;
                border-radius: 4px;
              }
              ::-webkit-scrollbar-thumb {
                background: #2f9e37;
                border-radius: 4px;
                transition: background-color 0.2s ease;
              }
              ::-webkit-scrollbar-thumb:hover {
                background: #238b2f;
              }
              ::-webkit-scrollbar-corner {
                background: #f1f1f1;
              }
              
              * {
                scrollbar-width: thin;
                scrollbar-color: #2f9e37 #f1f1f1;
              }
            </style>
          `;

          if (content.includes("</head>")) {
            content = content.replace("</head>", `${styleTag}</head>`);
          } else if (content.includes("<head>")) {
            content = content.replace("<head>", `<head>${styleTag}`);
          } else {
            content = content.replace(
              "<html>",
              `<html><head>${styleTag}</head>`
            );
          }
        } else {
          content = `
            <html>
              <head>
                <style>
                  body { 
                    margin: 0; 
                    padding: 0; 
                    font-family: system-ui, -apple-system, sans-serif;
                  }
                  
                  ::-webkit-scrollbar {
                    width: 8px;
                    height: 8px;
                  }
                  ::-webkit-scrollbar-track {
                    background: #f1f1f1;
                    border-radius: 4px;
                  }
                  ::-webkit-scrollbar-thumb {
                    background: #2f9e37;
                    border-radius: 4px;
                    transition: background-color 0.2s ease;
                  }
                  ::-webkit-scrollbar-thumb:hover {
                    background: #238b2f;
                  }
                  ::-webkit-scrollbar-corner {
                    background: #f1f1f1;
                  }
                  
                  * {
                    scrollbar-width: thin;
                    scrollbar-color: #2f9e37 #f1f1f1;
                  }
                </style>
              </head>
              <body>
                ${content}
              </body>
            </html>
          `;
        }
      }

      const blob = new Blob([content], { type: contentType });
      const url = URL.createObjectURL(blob);
      setPlotUrl(url);
      return () => {
        if (url) URL.revokeObjectURL(url);
      };
    }
  }, [plotData, isTable]);

  const parseResultData = (resultData) => {
    if (!resultData) return null;

    try {
      let headers = [];

      // Parse headers if header_string exists, this is legacy from before React implementation
      // plots should eventually all just return a list of headers under header_row
      if (resultData.header_string) {
        console.log("🔍 DEBUG: Raw header_string:", resultData.header_string);
        const wrappedHeaderString = `<table>${resultData.header_string}</table>`;
        console.log("🔍 DEBUG: Wrapped header_string:", wrappedHeaderString);

        const parser = new DOMParser();
        const doc = parser.parseFromString(wrappedHeaderString, "text/html");
        console.log("🔍 DEBUG: Parsed document:", doc);

        const headerElements = doc.querySelectorAll("th");
        console.log("🔍 DEBUG: Found th elements:", headerElements);
        console.log("🔍 DEBUG: Number of th elements:", headerElements.length);

        headers = Array.from(headerElements).map((th) => {
          const text = th.textContent.trim();
          console.log("🔍 DEBUG: Processing th element:", th, "Text:", text);
          return text;
        });

        console.log("✅ DEBUG: Final extracted headers:", headers);
        console.log("✅ DEBUG: Headers array length:", headers.length);
      }

      if (resultData.header_row) {
        headers = resultData.header_row;
      }

      // Process result_rows if they exist
      let rows = [];
      if (resultData.result_rows && Array.isArray(resultData.result_rows)) {
        rows = resultData.result_rows.map((row) => {
          return row.map((cell) => {
            if (typeof cell === "string" && cell.includes("<a href")) {
              const linkMatch = cell.match(
                /<a\s+href=['"]([^'"]*?)['"][^>]*?>(.*?)<\/a>/
              );
              if (linkMatch) {
                return {
                  type: "link",
                  text: linkMatch[2].trim(),
                  href: linkMatch[1],
                  target: "_blank",
                };
              }
            }

            if (typeof cell === "string" && cell.includes("<")) {
              return {
                type: "html",
                content: cell,
              };
            }

            return {
              type: "text",
              text: String(cell),
            };
          });
        });
      }

      return {
        title: "Analysis Results",
        short_code: resultData.short_code || null,
        headers: headers,
        rows: rows,
        fileName: resultData.file_name || null,
        csvHref: resultData.csv_href || null,
        totalCsvLines: resultData.total_csv_lines || null,
      };
    } catch (error) {
      console.error("Error parsing result data:", error);
      return null;
    }
  };

  const parseTeMessage = (message) => {
    if (!message || typeof message !== "string") return null;
    try {
      const parts = message.split("?~").filter((part) => part.trim());
      if (parts.length < 2) return null;
      let headers = [];
      const headerMatch = parts[0].match(
        /<thead>[\s\S]*?<tr>([\s\S]*?)<\/tr>[\s\S]*?<\/thead>/
      );
      if (headerMatch) {
        const headerContent = headerMatch[1];
        const thMatches = headerContent.match(/<th[^>]*>(.*?)<\/th>/g);
        if (thMatches) {
          headers = thMatches.map((th) => th.replace(/<[^>]*>/g, "").trim());
        }
      }
      if (headers.length === 0) {
        headers = [
          "Filename",
          "Gene",
          "5' UTR Count",
          "CDS count",
          "3' UTR count",
          "OTHER count",
          "View Plot",
        ];
      }
      const dataRows = [];
      for (let i = 1; i < parts.length; i++) {
        const rowData = parts[i].trim();
        if (!rowData) continue;
        const cells = rowData
          .split(";")
          .map((cell) => cell.trim())
          .filter((cell) => cell && cell !== ".")
          .filter((cell) => cell.length > 0);
        if (cells.length === 0) continue;
        const parsedRow = cells.map((cell) => {
          const linkMatch = cell.match(
            /<a\s+href=['"]([^'"]*?)['"][^>]*?>(.*?)<\/a>/
          );
          if (linkMatch) {
            return {
              type: "link",
              text: linkMatch[2].trim(),
              href: linkMatch[1],
              target: "_blank",
            };
          }
          return {
            type: "text",
            text: cell
              .replace(/<[^>]*>/g, "")
              .replace(/\.$/, "")
              .trim(),
          };
        });
        if (parsedRow.length >= 6) {
          dataRows.push(parsedRow);
        }
      }
      return {
        title: "Gene Expression Data",
        headers: headers,
        rows: dataRows,
      };
    } catch (error) {
      console.error("Error parsing TE message:", error);
      return null;
    }
  };

  const parseHtmlTable = (htmlString) => {
    if (!htmlString || typeof htmlString !== "string") return null;
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(htmlString, "text/html");
      const table = doc.querySelector("table");
      if (!table) return null;
      const titleElement = doc.querySelector("h1");
      const tableTitle = titleElement ? titleElement.textContent.trim() : "";
      const headerRow = table.querySelector("thead tr");
      const headers = Array.from(headerRow?.querySelectorAll("th") || []).map(
        (th) => th.textContent.trim()
      );
      const dataRows = Array.from(table.querySelectorAll("tbody tr, tr"))
        .filter((row) => !row.closest("thead"))
        .map((row) => {
          const cells = Array.from(row.querySelectorAll("td"));
          return cells.map((cell) => {
            const link = cell.querySelector("a");
            if (link) {
              return {
                type: "link",
                text: link.textContent.trim(),
                href: link.href,
                target: link.target || "_blank",
              };
            }
            return {
              type: "text",
              text: cell.textContent.trim(),
            };
          });
        });
      return {
        title: tableTitle,
        headers: headers,
        rows: dataRows,
      };
    } catch (error) {
      console.error("Error parsing HTML table:", error);
      return null;
    }
  };

  const renderTableData = () => {
    if (!plotData) return null;
    let parsedData = null;

    // Handle new result format with header_string and/or result_rows
    if (plotData.header_string || plotData.result_rows) {
      parsedData = parseResultData(plotData);
    }
    // Handle TE format (object with message)
    else if (typeof plotData === "object" && plotData.message) {
      parsedData = parseTeMessage(plotData.message);
    }
    // Handle HTML table format (unmapped reads)
    else if (typeof plotData === "string" && plotData.includes("<table")) {
      parsedData = parseHtmlTable(plotData);
    }


    // ✅ UPDATED: Render table even if no headers or no rows
    if (parsedData) {
      // if headers missing, create generic ones from first row
      const headers =
        parsedData.headers && parsedData.headers.length > 0
          ? parsedData.headers
          : parsedData.rows && parsedData.rows.length > 0
            ? Array.from({ length: parsedData.rows[0].length }, (_, i) => `Column ${i + 1}`)
            : [];

      const tableRows = (parsedData.rows || []).map((row, idx) => {
        const raw = row.map(cellToSortableText);
        return {
          id: idx,
          _cells: row,
          _raw: raw,
        };
      });

      const q = filterText.trim().toLowerCase();
      const filteredRows =
        q.length === 0
          ? tableRows
          : tableRows.filter((r) => r._raw.join(" ").toLowerCase().includes(q));

      const columns = headers.map((header, colIndex) => {
        const name = String(header);
        // list of headers for columns that should be smaller in width, things like width
        // maxWidth & grow will be set differently for these columns, these columns will
        // also not have any padding, rrts will match
        // only 'rrts' and not 'group 1 rrts' or similar. Same with stop/stop sequence,
        // count and 'rt count', etc.
        const isSmallCol =
          /probability/i.test(name) ||
          /^\s*count\s*$/i.test(name) ||
          /view/i.test(name) ||
          /position/i.test(name) ||
          /region/i.test(name) ||
          /start/i.test(name) ||
          /^\s*stop\s*$/i.test(name) ||
          /length/i.test(name) ||
          /type/i.test(name);

        // These columns have a larger width than small cols, but smaller than default
        // and do have padding
        const isMedCol =
          /rtcount/i.test(name) ||
          /gene/i.test(name) ||
          /pause score/i.test(name) ||
          /tran/i.test(name);

        const colWidth = isSmallCol ? "85px" : isMedCol ? "160px" : undefined;

        return {
          name,
          sortable: true,
          selector: (row) => row._raw[colIndex] ?? "",
          cell: (row) => renderCell(row._cells[colIndex]),
          compact: isSmallCol,
          maxWidth: colWidth,
          width: colWidth,
          grow: isSmallCol ? 0 : 2, 
          wrap: false,
        };
      });

      const showEmpty = headers.length === 0;

      return (

        <div
          style={{
            borderRadius: "8px",
            overflow: "hidden",
          }}
        >
            {/*Copy short link and download buttons, appears above table*/}
            <div className="d-flex align-items-center gap-2 p-2">
              {/*if there's a csvHref in parsedData, show a link at top left of table*/}
              {parsedData.csvHref && (
                  <a
                    href={parsedData.csvHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-outline-success btn-sm"
                    style={{
                      borderColor: "#2F9E37",
                      color: "#2F9E37",
                    }}
                  >
                    <i className="ri-download-line me-1"></i>
                    Download CSV
                    {parsedData.totalCsvLines && (
                      <span className="ms-1">
                        ({parsedData.totalCsvLines} lines)
                      </span>
                    )}
                  </a>
              )}

              {/*if there's a short code in parsedData, show a copy btn at top right of table*/}
              {parsedData.short_code && (
                  <button
                    type="button"
                    onClick={() => handleCopyShortCode(parsedData.short_code)}
                    className="btn btn-outline-success btn-sm ms-auto"
                    style={{ borderColor: "#2F9E37", color: "#2F9E37", marginRight: "5px"}}
                    title="Copy short code link to clipboard"
                  >
                    <i className="ri-clipboard-line me-1"></i>
                    {copied ? "Copied!" : "Copy short link"}
                  </button>

              )}
            </div>

          {showEmpty ? (
            <div
              style={{
                textAlign: "center",
                padding: "40px 20px",
                color: "#6c757d",
                fontStyle: "italic",
              }}
            >
              Table structure available but no data to display
            </div>
          ) : (
            <DataTable
              columns={columns}
              data={filteredRows}
              customStyles={dataTableStyles}
              sortFunction={customColumnSort}
              responsive
              dense
              striped
              highlightOnHover
              persistTableHead
              fixedHeader
              fixedHeaderScrollHeight="560px"
              pagination
              paginationPerPage={rowsPerPage}
              paginationRowsPerPageOptions={[10, 25, 50, 100]}
              onChangeRowsPerPage={(newPerPage) => setRowsPerPage(newPerPage)}
              subHeader
              subHeaderComponent={
                <div className="d-flex align-items-center gap-2 ms-auto">
                  {filterText && (
                    <button
                      type="button"
                      className="btn btn-outline-secondary btn-sm"
                      onClick={() => setFilterText("")}
                    >
                      Clear
                    </button>
                  )}
                  <input
                    className="form-control form-control-sm"
                    placeholder="Search table..."
                    value={filterText}
                    onChange={(e) => setFilterText(e.target.value)}
                    style={{ maxWidth: 320 }}
                  />

                </div>
              }
              noDataComponent={
                <div style={{ padding: 20, color: "#6c757d", fontStyle: "italic" }}>
                  No data available
                </div>
              }
            />
          )}
        </div>
      );
    }

    return (
      <div style={{ padding: "15px" }}>
        <div
          className="custom-scroll"
          style={{
            maxHeight: "600px",
            overflow: "auto",
            backgroundColor: "#f8f9fa",
            borderRadius: "8px",
            border: "1px solid #2F9E37",
          }}
        >
          <pre
            style={{
              padding: "20px",
              fontSize: "14px",
              lineHeight: "1.6",
              fontFamily: "monospace",
              margin: 0,
              color: "#495057",
            }}
          >
            {JSON.stringify(plotData, null, 2)}
          </pre>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <h5 className="mb-0">
            <i
              className={`${
                isTable ? "ri-table-line" : "ri-bar-chart-line"
              } me-2`}
            ></i>
            {title}
          </h5>
        </CardHeader>
        <CardBody className="text-center py-5">
          <Spinner style={{ color: "#2F9E37" }} size="lg" className="mb-3" />
          <h6>
            Generating {isTable ? "table" : "plot"}
            {showFileCount && selectedFilesCount > 0 && (
              <>
                {" "}
                with{" "}
                <strong style={{ color: "#2F9E37" }}>
                  {selectedFilesCount}
                </strong>{" "}
                files
              </>
            )}
            ...
          </h6>
          <p className="text-muted">This may take a few moments</p>
        </CardBody>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <h5 className="mb-0">
            <i className="ri-error-warning-line me-2"></i>
            {isTable ? "Table" : "Plot"} Generation Failed
          </h5>
        </CardHeader>
        <CardBody>
          <Alert color="danger" className="mb-3">
            {error}
          </Alert>
          <p className="text-muted">
            Please check your settings and selected files, then try again.
          </p>
          {onRetry && (
            <Button
              style={{
                backgroundColor: "#2F9E37",
                borderColor: "#2F9E37",
                color: "white",
              }}
              onClick={onRetry}
            >
              <i className="ri-refresh-line me-2"></i>
              Retry
            </Button>
          )}
        </CardBody>
      </Card>
    );
  }

  if (!plotData) {
    return (
      <Card style={{ border: "2px solid #e9ecef", borderRadius: "8px" }}>
        <CardHeader style={{ backgroundColor: "#f8f9fa", color: "#6b7280" }}>
          <h5 className="mb-0">
            <i
              className={`${
                isTable ? "ri-table-line" : "ri-bar-chart-line"
              } me-2`}
            ></i>
            {title}
          </h5>
        </CardHeader>
        <CardBody className="text-center py-5 text-muted">
          Select files and configure settings to generate a{" "}
          {isTable ? "table" : "plot"}
        </CardBody>
      </Card>
    );
  }

  return (
    <div ref={ref}>
      <Card>
        <CardBody style={{ padding: 0 }}>
          {isTable
            ? renderTableData()
            : plotUrl && (
                <DynamicIframe
                  src={plotUrl}
                  title={title}
                  className="custom-scroll"
                  style={{
                    width: "100%",
                    border: "none",
                    borderRadius: "0 0 6px 6px",
                  }}
                />
              )}
        </CardBody>
      </Card>

      <style jsx>{`
        .custom-scroll::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }
        .custom-scroll::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 4px;
        }
        .custom-scroll::-webkit-scrollbar-thumb {
          background: #2f9e37;
          border-radius: 4px;
          transition: background-color 0.2s ease;
        }
        .custom-scroll::-webkit-scrollbar-thumb:hover {
          background: #238b2f;
        }
        .custom-scroll::-webkit-scrollbar-corner {
          background: #f1f1f1;
        }
        .custom-scroll {
          scrollbar-width: thin;
          scrollbar-color: #2f9e37 #f1f1f1;
        }

        .table-hover tbody tr:hover {
          background-color: rgba(47, 158, 55, 0.05) !important;
        }

        iframe.custom-scroll {
          scrollbar-width: thin;
          scrollbar-color: #2f9e37 #f1f1f1;
        }
      `}</style>
    </div>
  );
});

export default PlotContainer;
